import { Group, NumberInput, Text, Stack, Box } from '@mantine/core';
import { useState, useEffect, useRef, FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  validateDay,
  validateMonth,
  validateYear,
  validateBirthDate,
  parseDateToParts,
  datePartsToISOString,
  getDaysInMonth,
} from '@utils/birth-date-validation';

// -----------------------------------------------------------------------------
export interface BirthDateInputProps {
  value?: string | Date | null;
  onChange?: (isoDateString: string | null) => void;
  onBlur?: () => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  label?: string;
  showAge?: boolean;
  showFieldLabels?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

// -----------------------------------------------------------------------------
export const BirthDateInput: FC<BirthDateInputProps> = ({
  value,
  onChange,
  onBlur,
  onKeyDown,
  error: externalError,
  required = false,
  disabled = false,
  label,
  showAge = true,
  showFieldLabels = true,
  size = 'md',
}: BirthDateInputProps) => {
  const { t } = useTranslation(['common']);

  // ---------------------------------------------------------------------------
  const initialParts = parseDateToParts(value);

  // ---------------------------------------------------------------------------
  const [dateFields, setDateFields] = useState<{
    day: number | string | undefined;
    month: number | string | undefined;
    year: number | string | undefined;
  }>({
    day: initialParts?.day,
    month: initialParts?.month,
    year: initialParts?.year,
  });

  const [fieldErrors, setFieldErrors] = useState<{
    day: string;
    month: string;
    year: string;
  }>({
    day: '',
    month: '',
    year: '',
  });

  const [touched, setTouched] = useState(false);

  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    const parts = parseDateToParts(value);
    if (parts) {
      setDateFields({
        day: parts.day,
        month: parts.month,
        year: parts.year,
      });
    } else if (value === null || value === undefined) {
      // Use empty string to properly clear Mantine NumberInput
      setDateFields({
        day: '',
        month: '',
        year: '',
      });
      setTouched(false);
      setFieldErrors({
        day: '',
        month: '',
        year: '',
      });
    }
  }, [value]);

  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!touched) {
      return;
    }

    const { day, month, year } = dateFields;
    setFieldErrors({
      day: '',
      month: '',
      year: '',
    });

    if (
      (day === undefined || day === '') &&
      (month === undefined || month === '') &&
      (year === undefined || year === '')
    ) {
      onChange?.(null);
      return;
    }

    if (
      (day !== undefined && day !== '') ||
      (month !== undefined && month !== '') ||
      (year !== undefined && year !== '')
    ) {
      const dayNum = typeof day === 'string' ? parseInt(day, 10) : day;
      const monthNum = typeof month === 'string' ? parseInt(month, 10) : month;
      const yearNum = typeof year === 'string' ? parseInt(year, 10) : year;

      const yearStr = year?.toString() || '';
      const isYearIncomplete = yearStr.length > 0 && yearStr.length < 4;

      if (isYearIncomplete) {
        return;
      }

      const newErrors = { day: '', month: '', year: '' };

      const dayVal = validateDay(dayNum, monthNum, yearNum);
      if (!dayVal.isValid && day !== undefined && day !== '') {
        newErrors.day = dayVal.error || '';
      }

      const monthVal = validateMonth(monthNum);
      if (!monthVal.isValid && month !== undefined && month !== '') {
        newErrors.month = monthVal.error || '';
      }

      const yearVal = validateYear(yearNum);
      if (!yearVal.isValid && year !== undefined && year !== '') {
        newErrors.year = yearVal.error || '';
      }

      if (
        dayNum !== undefined &&
        !isNaN(dayNum) &&
        monthNum !== undefined &&
        !isNaN(monthNum) &&
        yearNum !== undefined &&
        !isNaN(yearNum)
      ) {
        const validation = validateBirthDate(dayNum, monthNum, yearNum);
        if (validation.isValid) {
          const isoDate = datePartsToISOString(dayNum, monthNum, yearNum);
          onChange?.(isoDate);
        } else {
          // Show error on the most relevant field
          if (validation.error?.includes('day')) {
            newErrors.day = validation.error;
          } else if (validation.error?.includes('month')) {
            newErrors.month = validation.error;
          } else if (validation.error?.includes('year')) {
            newErrors.year = validation.error;
          } else {
            newErrors.year = validation.error || '';
          }
          onChange?.(null);
        }
      }

      setFieldErrors(newErrors);
      // If not all fields are filled, don't call onChange at all
      // This prevents clearing the form value while user is still typing
    }
  }, [dateFields, touched, onChange]);

  // ---------------------------------------------------------------------------
  const handleDayChange = (value: number | string) => {
    setTouched(true);
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

    if (isNaN(numValue) || value === '') {
      setDateFields((prev) => ({ ...prev, day: undefined }));
      return;
    }

    setDateFields((prev) => ({ ...prev, day: numValue }));

    // Auto-advance to month if day is valid and 2 digits entered
    if (numValue >= 1 && numValue <= 31 && String(numValue).length >= 2) {
      // Small delay to allow user to see the value
      setTimeout(() => monthRef.current?.focus(), 50);
    }
  };

  // ---------------------------------------------------------------------------
  const handleMonthChange = (value: number | string) => {
    setTouched(true);
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

    if (isNaN(numValue) || value === '') {
      setDateFields((prev) => ({ ...prev, month: undefined }));
      return;
    }

    setDateFields((prev) => ({ ...prev, month: numValue }));

    // Auto-advance to year if month is valid and 2 digits entered
    if (numValue >= 1 && numValue <= 12 && String(numValue).length >= 2) {
      setTimeout(() => yearRef.current?.focus(), 50);
    }
  };

  // ---------------------------------------------------------------------------
  const handleYearChange = (value: number | string) => {
    setTouched(true);
    const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

    if (isNaN(numValue) || value === '') {
      setDateFields((prev) => ({ ...prev, year: undefined }));
      return;
    }

    setDateFields((prev) => ({ ...prev, year: numValue }));
  };

  // ---------------------------------------------------------------------------
  const handleBlur = () => {
    setTouched(true);
    onBlur?.();
  };

  // ---------------------------------------------------------------------------
  const { day, month, year } = dateFields;
  const dayNum =
    typeof day === 'string'
      ? day === ''
        ? undefined
        : parseInt(day, 10)
      : day;
  const monthNum =
    typeof month === 'string'
      ? month === ''
        ? undefined
        : parseInt(month, 10)
      : month;
  const yearNum =
    typeof year === 'string'
      ? year === ''
        ? undefined
        : parseInt(year, 10)
      : year;

  const calculatedAge =
    dayNum !== undefined &&
    !isNaN(dayNum) &&
    monthNum !== undefined &&
    !isNaN(monthNum) &&
    yearNum !== undefined &&
    !isNaN(yearNum)
      ? validateBirthDate(dayNum, monthNum, yearNum).age
      : undefined;

  // ---------------------------------------------------------------------------
  const maxDays = monthNum && yearNum ? getDaysInMonth(monthNum, yearNum) : 31;

  // ---------------------------------------------------------------------------
  return (
    <Stack gap="2px">
      {label && (
        <Text component="label" size="sm" fw={500}>
          {label}
          {required && (
            <span style={{ color: 'var(--mantine-color-red-6)' }}> *</span>
          )}
        </Text>
      )}

      <Group gap="xs" align="flex-start" wrap="nowrap">
        <NumberInput
          ref={dayRef}
          label={showFieldLabels ? t('birth_date.day') : undefined}
          placeholder={t('birth_date.day_placeholder')}
          value={dateFields.day}
          onChange={handleDayChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          error={fieldErrors.day ? t(fieldErrors.day as any) : ''}
          min={1}
          max={maxDays}
          hideControls
          size={size}
          disabled={disabled}
          required={required}
          styles={{
            root: { flex: 1, minWidth: 40 },
            input: {
              textAlign: 'center',
              fontSize: size === 'lg' || size === 'xl' ? '1.1rem' : undefined,
            },
          }}
          maxLength={2}
          aria-label={t('birth_date.day')}
        />
        <NumberInput
          ref={monthRef}
          label={showFieldLabels ? t('birth_date.month') : undefined}
          placeholder={t('birth_date.month_placeholder')}
          value={dateFields.month}
          onChange={handleMonthChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          error={fieldErrors.month ? t(fieldErrors.month as any) : ''}
          min={1}
          max={12}
          hideControls
          size={size}
          disabled={disabled}
          required={required}
          styles={{
            root: { flex: 1, minWidth: 40 },
            input: {
              textAlign: 'center',
              fontSize: size === 'lg' || size === 'xl' ? '1.1rem' : undefined,
            },
          }}
          maxLength={2}
          aria-label={t('birth_date.month')}
        />
        <NumberInput
          ref={yearRef}
          label={showFieldLabels ? t('birth_date.year') : undefined}
          placeholder={t('birth_date.year_placeholder')}
          value={dateFields.year}
          onChange={handleYearChange}
          onBlur={handleBlur}
          onKeyDown={onKeyDown}
          error={fieldErrors.year ? t(fieldErrors.year as any) : ''}
          min={1900}
          max={new Date().getFullYear()}
          hideControls
          size={size}
          disabled={disabled}
          required={required}
          styles={{
            root: { flex: 1.5, minWidth: 60 },
            input: {
              textAlign: 'center',
              fontSize: size === 'lg' || size === 'xl' ? '1.1rem' : undefined,
            },
          }}
          maxLength={4}
          aria-label={t('birth_date.year')}
        />
      </Group>

      <Box mih={showAge ? '20px' : '0px'}>
        {showAge && calculatedAge !== undefined && calculatedAge >= 0 && (
          <Text size="sm" c="dimmed" mt={-4}>
            {t('birth_date.age')}: {calculatedAge} {t('birth_date.years_old')}
          </Text>
        )}
      </Box>

      {externalError && (
        <Text size="sm" c="red" mt={-4}>
          {externalError}
        </Text>
      )}
    </Stack>
  );
};
